import 'bootstrap/dist/css/bootstrap.css'
import 'font-awesome/css/font-awesome.css'
import 'animate.css/animate.min.css'
// Los tokens van antes que las hojas propias, para que éstas puedan usarlos.
import './assets/styles/tokens.css'
import './assets/styles/tokens.rutas.css'
import './assets/styles/style.css'
import './assets/styles/custom.css'

import posthog from 'posthog-js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect
} from 'react-router-dom'
import { Provider } from 'react-redux'

import store from './store'
import LoginContainer from './containers/LoginContainer'
import { ToastProvider } from './utils/toast'
import PantallaCargando from './components/01-atoms/PantallaCargando'
import entorno from './config/entorno'

/**
 * El panel se carga **después del login**, no antes.
 *
 * ─── Por qué (Fase 8) ───────────────────────────────────────────────────────
 *
 * Estaba importado de forma estática, así que todo lo que arrastra —los dos
 * contenedores del dashboard, la fachada de mapas con Leaflet, el datepicker,
 * los dieciséis módulos— acababa en el `main.js`.
 *
 * El efecto: **quien abre la pantalla de login descargaba el panel entero**
 * antes de poder escribir su contraseña. Medido, eso es la mayor parte de los
 * 488 kB comprimidos del paquete de entrada.
 *
 * Con `React.lazy` el login pesa lo que pesa un formulario, y el panel se
 * descarga en el momento de entrar — que es una espera que el usuario ya
 * asocia con «estoy entrando».
 *
 * La frontera de `<Suspense>` que esto necesita está más abajo, **dentro del
 * `<Router>`**. La primera versión la dejó fuera, dando por bueno que «ya
 * estaba puesta»: la aplicación quedaba en blanco al iniciar sesión. El porqué
 * está explicado donde se monta el árbol; no muevas esa frontera sin leerlo.
 */
const MainContainer = React.lazy(() => import('./containers/MainContainer'))

if (entorno.esProduccion && entorno.posthogClave) {
  posthog.init(entorno.posthogClave, {
    api_host: entorno.posthogHost || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_exceptions: true
  })
}

const ProtectedRouter = () => {
  const isAuthenticated = Boolean(localStorage.getItem('accessToken'))
  return isAuthenticated ? (
    <Route path="/" component={MainContainer} />
  ) : (
    <Redirect to="/login" />
  )
}

/**
 * Punto de entrada — API de raíz de React 18.
 *
 * `ReactDOM.render` sigue existiendo en la 18, pero en modo **legacy**: mantiene
 * el comportamiento de la 17 y desactiva el renderizado concurrente. Usar
 * `createRoot` es lo que activa de verdad la versión nueva, y es la única
 * llamada de este tipo en todo el proyecto — `xcomponent` recibe `ReactDOM`
 * pero nunca invoca `.render()` (comprobado sobre su código, ver
 * `OrderNew.test.js`).
 *
 * ─── `StrictMode`, sólo en desarrollo (Fase 7) ──────────────────────────────
 *
 * Quedó fuera del salto a propósito (decisión D5): su doble montaje destapa
 * fallos reales, y mezclarlo con la subida de React habría hecho imposible
 * distinguir un fallo preexistente de una regresión.
 *
 * Antes de activarlo se midió qué destapa, y la conclusión ordenó el trabajo:
 *
 * | Se duplica         | Coste real                                            |
 * | ------------------ | ----------------------------------------------------- |
 * | Una petición `GET` | Ninguno: StrictMode sólo corre en desarrollo y un GET  |
 * |                    | es idempotente. React lo hace a propósito.            |
 * | Un **socket**      | Dos conexiones vivas, cada mensaje procesado dos veces |
 * | Un **intervalo**   | Se acumulan, nunca se cancela el primero              |
 * | Un **listener**    | Ídem                                                   |
 *
 * Las tres últimas sobreviven al desmontaje, así que son las que importan. Se
 * comprobaron una a una: los sockets ya eran idempotentes desde la Fase 0
 * (H2), y de los efectos con temporizador aparecieron tres sin limpiar — dos
 * de ellos con un `history.push` diferido que arrastraba al operador a otra
 * pantalla aunque se hubiera ido.
 *
 * **No se envuelve en producción.** StrictMode no tiene efecto en el build de
 * producción, pero envolver el árbol añade nodos al reconciliador sin motivo.
 */
const contenedor = document.getElementById('root')
const raiz = createRoot(contenedor)

/**
 * ─── Por qué el `<Suspense>` va DENTRO del `<Router>` ───────────────────────
 *
 * Parece un detalle de anidamiento y no lo es. Con la frontera por fuera, la
 * aplicación quedaba **en blanco al entrar**, y así se descubrió en `pre`:
 *
 * 1. El operador inicia sesión y `LoginContainer` hace `history.push('/')`.
 * 2. `ProtectedRouter` renderiza `MainContainer`, que es `React.lazy`, y
 *    **suspende** mientras baja su paquete.
 * 3. La frontera que lo captura es la de arriba, que envuelve al `<Router>`:
 *    React esconde ese subárbol entero para pintar el mensaje de carga.
 * 4. Al llegar el paquete, el `<Redirect from="/" to="/orders" />` de
 *    `MainContainer` llama a `history.replace`. **La URL del navegador cambia,
 *    pero el `<Router>` escondido nunca aplica esa actualización.**
 * 5. Resultado: el navegador dice `/orders` y el `<Switch>` sigue evaluando
 *    contra `/`. Ninguna ruta casa, no se pinta nada y no se pide nada.
 *
 * Lo desconcertante para quien lo sufre es que pulsar el módulo en el menú lo
 * arregla: ese `history.push` sí llega, porque para entonces el Router ya está
 * visible.
 *
 * Con la frontera aquí dentro, el `<Router>` nunca se esconde y no pierde
 * ninguna navegación. Cubierto por `src/__tests__/enrutadoSuspense.test.js`.
 */
const arbol = (
  <Provider store={store}>
    <ToastProvider>
      <Router>
        <React.Suspense
          fallback={<PantallaCargando texto="Cargando el panel…" />}
        >
          <Switch>
            <Route path="/login" component={LoginContainer} />
            <ProtectedRouter />
          </Switch>
        </React.Suspense>
      </Router>
    </ToastProvider>
  </Provider>
)

raiz.render(
  entorno.esDesarrollo ? <React.StrictMode>{arbol}</React.StrictMode> : arbol
)

// registerServiceWorker()
